import { GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { RoleCols } from "../../../dummy-data/provider-roles-and-resp";
import useDisclosure from "../../../hooks/use-disclosure";
import { getAllRoles } from "../../../queries/tenant-setting/use-get-roles-previllage-list";
import { useRoleArchive } from "../../../queries/tenant-setting/use-role-archive";
import { useRoleStatusChange } from "../../../queries/tenant-setting/use-role-status";
import {
  applyDefaultColumnSettings,
  DEF_PAGINATION,
  DGT_Pagination,
  getXtenantId,
  replaceUnderScoreAddSpace,
} from "../../../utils/utils";
import CustomDataGridV2 from "../../CustomDataGridV2";
import { GlobalTableHeightWrapper } from "../../GlobalTableHeightWrapper";
import ToggleSwitch from "../../ToggleSwitch";
import ActionMenu from "../ActionMenu";
import DeleteModal, { useDeleteDisClosure } from "../DeleteModal";
import UpsertRoleModal, { Action } from "./user-settings/UpsertRoleModal";

type ProviderRolesAndResponsibilityProps = {
  archived?: boolean;
};

const ProviderRolesAndResponsibility = ({
  archived,
}: ProviderRolesAndResponsibilityProps) => {
  const [roleDetail, setRoleDetail] = useState<Partial<RoleCols>>();
  const [action, setAction] = useState<Action>("view");
  const outletContext = useOutletContext<{ isArchieved: boolean }>();
  const {
    opened: openedViewRoleModel,
    open: openViewRoleModel,
    close: closeViewRoleModel,
  } = useDisclosure(false);
  const deleteRoleDisclosure = useDeleteDisClosure();
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [_, setGridSorting] = useState<GridSortItem[]>([]);
  const { data: rolesData, refetch } = getAllRoles({
    pagination,
    archive: archived ?? !!outletContext?.isArchieved,
    xTENANTID: getXtenantId(),
    // roleType: "PROVIDER",
  });
  const { mutateAsync: updateRoleStatus } = useRoleStatusChange();
  const { mutateAsync: roleArchive } = useRoleArchive();

  const isCurrentlyArchived = !!archived || !!outletContext?.isArchieved;
  const actionLabel = isCurrentlyArchived ? "Unarchive" : "Archive";
  const actionLabelLower = actionLabel.toLowerCase();

  function toggleStatus(value: boolean, id: number) {
    return updateRoleStatus({
      roleId: id,
      status: value,
    }).then((res) => {
      refetch();
      return res;
    });
  }

  function archiveHandler<T>(_: boolean, id: T) {
    return roleArchive({
      roleId: id ?? ("" as any),
      status: !isCurrentlyArchived,
    }).then((res) => {
      refetch();
      return res;
    });
  }

  const COLUMNS = applyDefaultColumnSettings<RoleCols>([
    {
      field: "name",
      headerName: "Role",
      flex: 0.6,
      width: 240,
      sortable: false,
    },

    {
      field: "type",
      headerName: "Base Role",
      flex: 0.4,
      width: 240,
      sortable: false,
      renderCell: ({ value }: any) =>
        value ? replaceUnderScoreAddSpace(value?.toLowerCase()) : "-",
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      width: 200,
      sortable: false,
      renderCell: ({ value }: any) => value,
    },
    {
      field: "active",
      headerName: "Status",
      flex: 0.3,
      width: 120,
      sortable: false,
      renderCell: ({ row, value }) =>
        row?.name !== "SUPER_ADMIN" && (
          <ToggleSwitch
            id={row.id.toString()}
            content={"Are you sure you want to change the status of this role?"}
            checked={value}
            onConfirm={(v, id) => toggleStatus(v, Number(id))}
          />
        ),
    },

    {
      field: "Action",
      width: 90,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <ActionMenu
            options={[
              { value: "view", label: "View" },
              { value: "update", label: "Update" },
              {
                value: "archive",
                label: `${
                  !!archived || !!outletContext?.isArchieved
                    ? "Unarchive Role"
                    : "Archive Role"
                }`,
                isDisabled: (row as any)?.active || row?.name === "SUPER_ADMIN",
                hoverMsg: `${
                  row?.name === "SUPER_ADMIN"
                    ? "Cannot Archive Super Admin"
                    : "Disable Role to access this feature"
                }`,
              },
            ]}
            onActionClick={(v) => {
              if (v === "archive") {
                deleteRoleDisclosure.open(row.id);
              } else {
                if (v === "view") {
                  setAction("view");
                  setRoleDetail(row);
                } else if (v === "update") {
                  setAction("update");
                  setRoleDetail(row);
                }
                openViewRoleModel();
              }
            }}
          />
        );
      },
    },
  ]);

  return (
    <>
      <DeleteModal
        w="380px"
        open={deleteRoleDisclosure.opened}
        onClose={deleteRoleDisclosure.close}
        id={deleteRoleDisclosure.deleteId}
        onConfirmation={(id) => archiveHandler(true, id)}
        content={`Are you sure you want to ${actionLabelLower} this role?`}
        confirmButtonText={`Yes, ${actionLabel}`}
        buttonColor="warning"
      />
      {openedViewRoleModel && (
        <UpsertRoleModal
          onEditClick={() => setAction("update")}
          action={action}
          titleDetail={roleDetail}
          opened={openedViewRoleModel}
          onClose={closeViewRoleModel}
          prefillValue={{
            id: roleDetail?.id,
            name: roleDetail?.name,
            type: roleDetail?.type
              ? {
                  label: replaceUnderScoreAddSpace(roleDetail?.type),
                  value: roleDetail?.type,
                }
              : undefined,
            description: roleDetail?.description,
          }}
        />
      )}

      <GlobalTableHeightWrapper small marginTop="40px">
        <CustomDataGridV2
          getRowId={(row) => row?.id}
          rows={rolesData?.data?.content ?? []}
          rowCount={rolesData?.data?.page?.totalElements ?? 10}
          columns={COLUMNS}
          disableRowSelectionOnClick
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          onSortModelChange={setGridSorting}
        />
      </GlobalTableHeightWrapper>
    </>
  );
};

export default ProviderRolesAndResponsibility;
